import { PASSWORD_MIN_LENGTH, type Role, canApprove } from '@bjj/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { ErrorMessage, Field } from '../components/ui';

function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  const { settings } = useAuth();
  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div>
          {settings?.logoUrl ? (
            <img className="logo" src={settings.logoUrl} alt={`${settings.gymName} logo`} />
          ) : null}
          <div className="eyebrow">{settings?.curriculumTitle ?? 'Curriculum'}</div>
          <div className="gym">{settings?.gymName ?? ''}</div>
          <div className="belt" aria-hidden="true" />
        </div>
        <p className="foot">Staff dashboard · curriculum, students and grades</p>
      </div>
      <div className="auth-panel">
        <div className="auth-card">
          <h1>{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Tokens are delivered in the URL fragment (never sent to servers or logged). */
export function tokenFromHash(hash: string): string | null {
  const match = /(?:^#|&)token=([A-Za-z0-9_-]+)/.exec(hash);
  return match ? match[1]! : null;
}

/** Only instructors and owners use the dashboard; students and parents use the app. */
const isStaffRole = canApprove;

export function LoginPage() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useMutation({
    mutationFn: async () => {
      const u = await api.login(email, password);
      if (!isStaffRole(u.role)) {
        await api.logout();
        throw new Error(
          'The admin dashboard is for staff. Students and parents use the mobile app.',
        );
      }
      return u;
    },
    onSuccess: (u) => {
      const from =
        (location.state as { from?: string } | null)?.from ??
        (u.role === 'owner' ? '/' : '/students');
      navigate(from, { replace: true });
    },
  });
  if (ready && user && isStaffRole(user.role)) return <Navigate to="/" replace />;

  return (
    <AuthCard title="Sign in">
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          login.mutate();
        }}
      >
        <ErrorMessage error={login.error} />
        <Field label="Email">
          {(id) => (
            <input
              id={id}
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <Field label="Password">
          {(id) => (
            <input
              id={id}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <button
          className="btn primary"
          type="submit"
          disabled={login.isPending}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p style={{ marginTop: 14 }}>
        <Link to="/forgot-password">Forgot your password?</Link>
      </p>
    </AuthCard>
  );
}

function PasswordFields({
  password,
  setPassword,
  confirm,
  setConfirm,
}: {
  password: string;
  setPassword: (v: string) => void;
  confirm: string;
  setConfirm: (v: string) => void;
}) {
  return (
    <>
      <Field label="New password" hint={`At least ${PASSWORD_MIN_LENGTH} characters.`}>
        {(id) => (
          <input
            id={id}
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      <Field label="Confirm password">
        {(id) => (
          <input
            id={id}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        )}
      </Field>
    </>
  );
}

export function ActivatePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useAuth();
  const token = tokenFromHash(location.hash);
  const [name, setName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [doneRole, setDoneRole] = useState<Role | null>(null);

  const preview = useQuery({
    queryKey: ['invitation', token],
    enabled: Boolean(token),
    retry: false,
    queryFn: () => api.previewInvitation(token!),
  });
  const accept = useMutation({
    mutationFn: async () => {
      if (password !== confirm) throw new Error('Passwords do not match');
      return api.acceptInvitation(token!, name ?? preview.data!.name, password);
    },
    onSuccess: async (u) => {
      if (!isStaffRole(u.role)) {
        await api.logout();
        setDoneRole(u.role);
      } else {
        navigate(u.role === 'owner' ? '/' : '/students', { replace: true });
      }
    },
  });

  if (doneRole === 'student' || doneRole === 'parent') {
    return (
      <AuthCard title="Your account is active">
        <p>
          Download the <strong>{settings?.appDisplayName ?? 'curriculum'}</strong> app and sign in
          with <strong>{preview.data?.email}</strong> and the password you just created.
        </p>
      </AuthCard>
    );
  }
  if (!token)
    return (
      <AuthCard title="Invitation link missing">
        Open the activation link from your invitation email.
      </AuthCard>
    );
  if (preview.isLoading) return <AuthCard title="Checking invitation…">One moment.</AuthCard>;
  if (preview.error) {
    return (
      <AuthCard title="Invitation unavailable">
        <ErrorMessage error={preview.error} />
        <p>Ask your academy to send a new invitation.</p>
      </AuthCard>
    );
  }
  return (
    <AuthCard title="Activate your account">
      <p className="muted">
        Invitation for <strong>{preview.data?.email}</strong>
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          accept.mutate();
        }}
      >
        <ErrorMessage error={accept.error} />
        <Field label="Your name">
          {(id) => (
            <input
              id={id}
              type="text"
              required
              maxLength={80}
              value={name ?? preview.data?.name ?? ''}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <PasswordFields
          password={password}
          setPassword={setPassword}
          confirm={confirm}
          setConfirm={setConfirm}
        />
        <button
          className="btn primary"
          type="submit"
          disabled={accept.isPending}
          style={{ width: '100%', justifyContent: 'center' }}
        >
          {accept.isPending ? 'Activating…' : 'Activate account'}
        </button>
      </form>
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const request = useMutation({
    mutationFn: () => api.requestPasswordReset(email),
  });
  return (
    <AuthCard title="Reset your password">
      {request.isSuccess ? (
        <div className="alert success">
          If an active account exists for that email, a reset link is on its way.
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            request.mutate();
          }}
        >
          <ErrorMessage error={request.error} />
          <Field label="Email">
            {(id) => (
              <input
                id={id}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <button className="btn primary" type="submit" disabled={request.isPending}>
            Send reset link
          </button>
        </form>
      )}
      <p style={{ marginTop: 14 }}>
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const token = tokenFromHash(useLocation().hash);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const reset = useMutation({
    mutationFn: async () => {
      if (password !== confirm) throw new Error('Passwords do not match');
      await api.resetPassword(token!, password);
    },
  });
  if (!token)
    return (
      <AuthCard title="Reset link missing">Open the link from your password reset email.</AuthCard>
    );
  return (
    <AuthCard title="Choose a new password">
      {reset.isSuccess ? (
        <div className="alert success">
          Password updated. You can now sign in to the app or dashboard. All other devices were
          signed out.
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            reset.mutate();
          }}
        >
          <ErrorMessage error={reset.error} />
          <PasswordFields
            password={password}
            setPassword={setPassword}
            confirm={confirm}
            setConfirm={setConfirm}
          />
          <button className="btn primary" type="submit" disabled={reset.isPending}>
            Update password
          </button>
        </form>
      )}
    </AuthCard>
  );
}
